import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CalendarCheck,
  EnvelopeSimple,
  ImageSquare,
  MagicWand,
  NotePencil,
  PaperPlaneTilt,
  Sparkle,
  Trash,
  UploadSimple,
  VideoCamera,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

import api from '../services/api';
import { formatEventNameList } from '../config/eventTypes';
import { resolveMediaUrl } from '../utils/media';
import AIPremiumButton, { AI_USAGE_IMAGE_GENERATION } from './AIPremiumGate';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogDescription,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Textarea } from './ui/textarea';
import useEventTypeConfig from '../hooks/useEventTypeConfig';

const AUDIENCE_SIDES = [
  { value: 'default', label: 'Default' },
  { value: 'bride', label: 'Bride side' },
  { value: 'groom', label: 'Groom side' },
  { value: 'both', label: 'Both sides' },
];

const LANGUAGE_OPTIONS = [
  { value: 'english', label: 'English' },
  { value: 'hinglish', label: 'Hinglish' },
  { value: 'hindi', label: 'Hindi' },
];

const PERSON_STYLE_OPTIONS = [
  { value: 'real_photo', label: 'Real person' },
  { value: 'caricature', label: 'Caricature' },
];

const CREATIVITY_OPTIONS = [
  { value: 'balanced', label: 'Balanced' },
  { value: 'bold', label: 'Bold' },
  { value: 'experimental', label: 'Wild card' },
];

const LOOK_LOADING_COPY = [
  {
    title: 'Reading the reference photo',
    copy: 'Locking the people count so no surprise guest sneaks into the invite.',
  },
  {
    title: 'Tracing the design DNA',
    copy: 'Matching the uploaded inspiration layout, palette, borders, and art style.',
  },
  {
    title: 'Mixing the celebration colors',
    copy: 'Balancing florals, typography, spacing, and ceremony details.',
  },
  {
    title: 'Polishing the final artwork',
    copy: 'Rendering your high-quality image invite.',
  },
];

// Keep the request alive independently of this page component. React unmounts
// Invite Studio during navigation, but this module remains loaded and lets the
// next mount reconnect to the same request instead of starting over.
const inviteGenerationJobs = new Map();

const startInviteGenerationJob = (eventId, requestFactory, metadata) => {
  const existing = inviteGenerationJobs.get(eventId);
  if (existing?.status === 'pending') return existing;

  const job = {
    eventId,
    metadata,
    status: 'pending',
    response: null,
    error: null,
    promise: null,
  };
  job.promise = requestFactory()
    .then((response) => {
      job.status = 'success';
      job.response = response;
      return job;
    })
    .catch((error) => {
      job.status = 'error';
      job.error = error;
      return job;
    });
  inviteGenerationJobs.set(eventId, job);
  return job;
};

const sideLabel = (value) => AUDIENCE_SIDES.find((side) => side.value === value)?.label || 'Default';

const formatDate = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value).slice(0, 10);
  return parsed.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const selectedEventsKey = (ids = []) => (
  Array.isArray(ids) && ids.length ? ids.join('+') : 'selected'
);

const slotKey = ({ invite_scope, itinerary_event_id, itinerary_event_ids, audience_side }) => (
  invite_scope === 'selected_events'
    ? `${invite_scope}:${selectedEventsKey(itinerary_event_ids)}:${audience_side}`
    : `${invite_scope}:${itinerary_event_id || 'full'}:${audience_side}`
);

const slotKeyFromParts = (scope, itineraryEventId, audienceSide, itineraryEventIds = []) => (
  scope === 'selected_events'
    ? `${scope}:${selectedEventsKey(itineraryEventIds)}:${audienceSide}`
    : `${scope}:${itineraryEventId || 'full'}:${audienceSide}`
);

const draftCacheKeyFromParts = (scope, itineraryEventId, audienceSide, itineraryEventIds = []) => (
  scope === 'selected_events'
    ? 'selected_events:draft'
    : slotKeyFromParts(scope, itineraryEventId, audienceSide, itineraryEventIds)
);

const draftCacheKey = (context, audienceSide) => draftCacheKeyFromParts(
  context.scope,
  context.itineraryEventId,
  audienceSide,
  context.itineraryEventIds
);

const LAST_CREATED_DRAFT_CACHE_KEY = 'last-created-invite-draft';

const formCacheKey = (context, audienceSide, existing) => (
  existing?.id ? `invite:${existing.id}` : draftCacheKey(context, audienceSide)
);

const normalizeDraftForContext = (draft, context, audienceSide) => ({
  ...draft,
  title: invitationTitle(draft.title, context.title),
  audience_side: audienceSide,
  itinerary_event_ids: context.scope === 'selected_events'
    ? (draft.itinerary_event_ids || context.itineraryEventIds || [])
    : (context.itineraryEventIds || []),
});

const contextAudienceSide = (context, fallback = 'default') => {
  if (context?.scope === 'event') {
    const normalized = String(context.side || fallback || 'both').toLowerCase();
    return ['bride', 'groom', 'both'].includes(normalized) ? normalized : 'both';
  }
  if (context?.scope === 'selected_events') {
    return fallback || 'both';
  }
  return fallback || 'default';
};

const itemMatchesContext = (item, context) => {
  if (!item || !context) return false;
  const bothFullEventScopes = ['full_event', 'full_wedding'].includes(item.invite_scope)
    && ['full_event', 'full_wedding'].includes(context.scope);
  if (item.invite_scope !== context.scope && !bothFullEventScopes) return false;
  if (context.scope === 'event') return item.itinerary_event_id === context.itineraryEventId;
  if (context.scope === 'selected_events') return selectedEventsKey(item.itinerary_event_ids || []) === selectedEventsKey(context.itineraryEventIds || []);
  return ['full_event', 'full_wedding'].includes(context.scope);
};

const isInviteVisibleOnMain = (item) => (
  Boolean(item?.media_url || item?.is_generating || item?.generation_status === 'ready_to_select' || item?.generation_status === 'draft_only')
);

const buildPendingInviteItem = ({ id, payload, context, audienceSide, candidates = [] }) => {
  const firstCandidate = candidates.find((candidate) => candidate?.media_url);
  return {
    id,
    event_id: payload.event_id,
    invite_scope: payload.invite_scope,
    itinerary_event_id: payload.invite_scope === 'event' ? payload.itinerary_event_id : null,
    itinerary_event_ids: payload.invite_scope === 'selected_events' ? (payload.itinerary_event_ids || []) : [],
    audience_side: audienceSide,
    source: 'ai_draft',
    media_type: 'image',
    media_url: null,
    preview_media_url: firstCandidate?.media_url || null,
    generation_status: candidates.length ? 'ready_to_select' : 'generating',
    is_generating: !candidates.length,
    generated_candidates: candidates,
    title: firstCandidate?.title || payload.title || context.title,
    caption: firstCandidate?.caption || payload.caption || 'AI is creating an invite from the submitted form.',
    ai_inputs: payload.ai_inputs || {},
    is_deleted: false,
  };
};

const invitationTitle = (title, fallback) => (
  !title || /^Full (wedding|baby shower|birthday|event) invite$/i.test(title.trim()) ? fallback : title
);

const defaultDraft = (eventData, context, audienceSide, existing) => ({
  title: invitationTitle(existing?.title, context.title),
  caption: existing?.caption || '',
  ai_style: existing?.ai_style || 'royal',
  ai_language: existing?.ai_language || 'english',
  ai_format: 'image',
  family_line: existing?.ai_inputs?.family_line || '',
  blessing: existing?.ai_inputs?.blessing || '',
  hashtag: existing?.ai_inputs?.hashtag || '',
  notes: existing?.ai_inputs?.notes || '',
  bride_name: existing?.ai_inputs?.bride_name || eventData?.bride_name || '',
  groom_name: existing?.ai_inputs?.groom_name || eventData?.groom_name || '',
  host_names: existing?.ai_inputs?.host_names || (
    eventData?.event_type === 'birthday'
      ? formatEventNameList(eventData?.honoree_names, ', ')
      : eventData?.host_names?.join(', ')
  ) || '',
  reference_photo_url: existing?.ai_inputs?.reference_photo_url || '',
  reference_photo_filename: existing?.ai_inputs?.reference_photo_filename || '',
  inspiration_photo_url: existing?.ai_inputs?.inspiration_photo_url || '',
  inspiration_photo_filename: existing?.ai_inputs?.inspiration_photo_filename || '',
  person_style: existing?.ai_inputs?.person_style === 'avatar'
    ? 'caricature'
    : (existing?.ai_inputs?.person_style || 'caricature'),
  creativity: existing?.ai_inputs?.creativity || 'bold',
  look_count: '1',
  audience_side: audienceSide,
  itinerary_event_ids: existing?.itinerary_event_ids || context.itineraryEventIds || [],
});

function GeneratingInvitePreview({ step = 0 }) {
  const message = LOOK_LOADING_COPY[step % LOOK_LOADING_COPY.length];
  return (
    <div className="relative grid aspect-[4/3] place-items-center overflow-hidden rounded-md border bg-[linear-gradient(145deg,#fff8db,#f4c84a_45%,#f7efe2)] text-center">
      <div className="absolute inset-0 opacity-70 [background-image:repeating-linear-gradient(45deg,rgba(255,255,255,0.42)_0,rgba(255,255,255,0.42)_7px,transparent_7px,transparent_16px)]" />
      <div className="relative mx-4 rounded-md border bg-white/55 px-4 py-3 shadow-sm">
        <Sparkle className="mx-auto h-7 w-7 animate-pulse text-primary" weight="fill" />
        <p className="mt-2 text-sm font-semibold text-foreground">{message.title}</p>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">{message.copy}</p>
      </div>
    </div>
  );
}

function InvitePreview({ item, onPreview, loadingStep = 0 }) {
  if (item?.is_generating) {
    return <GeneratingInvitePreview step={loadingStep} />;
  }

  if (item?.generation_status === 'ready_to_select' && item?.preview_media_url) {
    const mediaUrl = resolveMediaUrl(item.preview_media_url, { preferProxy: true });
    return (
      <button
        type="button"
        className="group relative block w-full overflow-hidden rounded-md text-left focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        onClick={onPreview}
        title="Review invitation design"
        aria-label={`Review invitation design for ${item.title || 'invite media'}`}
      >
        <img
          src={mediaUrl}
          alt={item.title || 'Generated invite look'}
          className="aspect-[4/3] w-full rounded-md border object-cover transition group-hover:brightness-95"
        />
        <span className="absolute inset-x-2 bottom-2 rounded-md bg-background/90 px-2 py-1 text-center text-xs font-semibold shadow-sm">
          Design ready - review and select
        </span>
      </button>
    );
  }

  if (!item?.media_url) {
    return (
      <div className="grid aspect-[4/3] place-items-center rounded-md border border-dashed bg-muted/35 text-muted-foreground">
        <Sparkle className="h-6 w-6" weight="fill" />
      </div>
    );
  }

  const mediaUrl = resolveMediaUrl(item.media_url, { preferProxy: true });
  if (item.media_type === 'video') {
    return (
      <button
        type="button"
        className="group block w-full rounded-md text-left focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        onClick={onPreview}
        title="Open full invite"
        aria-label={`Open full invite preview for ${item.title || 'invite media'}`}
      >
        <video className="aspect-[4/3] w-full rounded-md border object-cover transition group-hover:brightness-95" src={mediaUrl}>
          <track kind="captions" />
        </video>
      </button>
    );
  }

  return (
    <button
      type="button"
      className="group block w-full rounded-md text-left focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      onClick={onPreview}
      title="Open full invite"
      aria-label={`Open full invite preview for ${item.title || 'invite media'}`}
    >
      <img
        src={mediaUrl}
        alt={item.title || 'Invite media'}
        className="aspect-[4/3] w-full rounded-md border object-cover transition group-hover:brightness-95"
      />
    </button>
  );
}

function InviteSlotCard({
  context,
  side,
  item,
  onGenerate,
  onDelete,
  onPreview,
  onHistory,
  loadingStep,
  onChoose,
}) {
  const hasMedia = Boolean(item?.media_url);
  const isGenerating = Boolean(item?.is_generating);
  const isReadyToSelect = item?.generation_status === 'ready_to_select';

  return (
    <div className="rounded-md border bg-card p-3 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-foreground">{side.label}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {isGenerating ? 'Generating AI looks' : isReadyToSelect ? 'Ready to choose' : item ? (item.source === 'manual' ? 'Manual media' : 'AI draft') : 'Not prepared'}
            {context.scope === 'selected_events' && context.subtitle ? ` | ${context.subtitle}` : ''}
          </p>
        </div>
        {item?.media_type === 'video' ? (
          <VideoCamera className="mt-0.5 h-4 w-4 text-muted-foreground" />
        ) : (
          <ImageSquare className="mt-0.5 h-4 w-4 text-muted-foreground" />
        )}
      </div>

      <div className="mt-3">
        <InvitePreview
          item={item}
          loadingStep={loadingStep}
          onPreview={() => (
            isReadyToSelect
              ? onChoose(context, side.value, item)
              : onPreview(context, side.value, item)
          )}
        />
      </div>

      <div className="mt-3">
        <p className="line-clamp-1 text-sm font-medium">{invitationTitle(item?.title, context.title)}</p>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {isReadyToSelect && (
          <Button type="button" size="sm" onClick={() => onChoose(context, side.value, item)}>
            Review design
          </Button>
        )}
        {item?.media_url && !isReadyToSelect && (
          <Button type="button" size="sm" variant="outline" onClick={() => onPreview(context, side.value, item)}>
            Preview
          </Button>
        )}
        {item?.source === 'ai_draft' && hasMedia && (
          <Button type="button" size="sm" variant="outline" onClick={() => onHistory(item, context, side.value)}>
            History
          </Button>
        )}
        <Button type="button" size="sm" variant="outline" onClick={() => onGenerate(context, side.value, item)} disabled={isGenerating}>
          <MagicWand className="h-4 w-4" />
          {isGenerating ? 'Generating' : hasMedia || isReadyToSelect ? 'Replace' : 'Generate'}
        </Button>
        {item && !isGenerating && !isReadyToSelect && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={() => onDelete(item)}
          >
            <Trash className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

function InviteContextTile({
  context,
  badge,
  children,
}) {
  const Icon = context.icon;
  return (
    <section className="min-w-0 rounded-md border bg-background">
      <div className="flex min-w-0 items-start justify-between gap-3 border-b px-3 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md border bg-muted/40">
            <Icon className="h-4 w-4 text-primary" />
          </span>
          <div className="min-w-0">
            <h3 className="truncate font-semibold text-foreground">{context.title}</h3>
            <p className="truncate text-xs text-muted-foreground">{context.subtitle || 'Event invitation'}</p>
          </div>
        </div>
        {badge}
      </div>
      <div className="grid gap-3 p-3">
        {children}
      </div>
    </section>
  );
}

function LoadingLookCard({ index, step }) {
  const message = LOOK_LOADING_COPY[(step + index) % LOOK_LOADING_COPY.length];
  const orbit = LOOK_LOADING_COPY[(step + index + 1) % LOOK_LOADING_COPY.length];
  return (
    <div className="overflow-hidden rounded-md border bg-card">
      <div className="relative aspect-[4/5] overflow-hidden bg-[linear-gradient(145deg,#fff8db,#f5c842_45%,#f4efe5)]">
        <div className="absolute inset-0 opacity-70 [background-image:radial-gradient(circle_at_20%_18%,rgba(255,255,255,0.85),transparent_18%),radial-gradient(circle_at_78%_82%,rgba(255,255,255,0.65),transparent_20%)]" />
        <div className="absolute inset-x-5 top-6 h-16 rounded-md border border-white/60 bg-white/45 shadow-sm" />
        <div className="absolute inset-x-8 top-28 h-2 rounded-full bg-white/70" />
        <div className="absolute inset-x-14 top-36 h-2 rounded-full bg-white/55" />
        <div className="absolute bottom-8 left-1/2 grid h-28 w-28 -translate-x-1/2 place-items-center rounded-full border border-white/70 bg-white/40 shadow-inner">
          <Sparkle className="h-8 w-8 animate-pulse text-primary" weight="fill" />
        </div>
        <div className="absolute inset-y-0 left-0 w-10 bg-[repeating-linear-gradient(45deg,rgba(255,255,255,0.45)_0,rgba(255,255,255,0.45)_8px,transparent_8px,transparent_16px)]" />
        <div className="absolute inset-y-0 right-0 w-10 bg-[repeating-linear-gradient(135deg,rgba(255,255,255,0.45)_0,rgba(255,255,255,0.45)_8px,transparent_8px,transparent_16px)]" />
        <div className="absolute inset-0 animate-pulse bg-white/10" />
      </div>
      <div className="space-y-3 p-3">
        <div>
          <p className="line-clamp-1 text-sm font-semibold">{message.title}</p>
          <p className="mt-1 line-clamp-2 text-xs leading-5 text-muted-foreground">{message.copy}</p>
        </div>
        <div className="flex items-center gap-2 rounded-md border bg-muted/25 px-3 py-2 text-xs font-medium text-muted-foreground">
          <MagicWand className="h-4 w-4 text-primary" />
          {orbit.title}
        </div>
      </div>
    </div>
  );
}

export default function InviteStudio({ eventId, eventData }) {
  const eventTypeConfig = useEventTypeConfig(eventData);
  const displaySideLabel = useCallback(
    (value) => eventTypeConfig.features.showGuestSides ? sideLabel(value) : 'All guests',
    [eventTypeConfig.features.showGuestSides]
  );
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draftSlot, setDraftSlot] = useState(null);
  const [draftForm, setDraftForm] = useState({});
  const [draftCache, setDraftCache] = useState({});
  const [savingDraft, setSavingDraft] = useState(false);
  const [previewSlot, setPreviewSlot] = useState(null);
  const [uploadingReferencePhoto, setUploadingReferencePhoto] = useState(false);
  const [uploadingInspirationPhoto, setUploadingInspirationPhoto] = useState(false);
  const [creatingLooks, setCreatingLooks] = useState(false);
  const [loadingLookStep, setLoadingLookStep] = useState(0);
  const [lookCandidates, setLookCandidates] = useState([]);
  const [chooseSlot, setChooseSlot] = useState(null);
  const [selectingLookId, setSelectingLookId] = useState(null);
  const [historySlot, setHistorySlot] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyCandidates, setHistoryCandidates] = useState([]);
  const [generationJobVersion, setGenerationJobVersion] = useState(0);

  const contexts = useMemo(() => {
    const itinerary = Array.isArray(eventData?.itinerary) ? eventData.itinerary : [];
    return [
      {
        scope: eventTypeConfig.isWedding ? 'full_wedding' : 'full_event',
        itineraryEventId: null,
        title: eventData?.name || (
          eventTypeConfig.isWedding && (eventData?.bride_name || eventData?.groom_name)
            ? `${[eventData?.bride_name, eventData?.groom_name].filter(Boolean).join(' & ')}'s Wedding`
            : eventTypeConfig.isBirthday && formatEventNameList(eventData?.honoree_names)
              ? `${formatEventNameList(eventData.honoree_names)}'s Birthday`
              : eventTypeConfig.labels.fullEventInvite
        ),
        subtitle: eventTypeConfig.isWedding
          ? `${eventData?.bride_name || 'Bride'} & ${eventData?.groom_name || 'Groom'}`
          : eventTypeConfig.isBirthday
            ? formatEventNameList(eventData?.honoree_names) || eventData?.name || eventTypeConfig.label
          : (eventData?.host_names || []).join(' & ') || eventData?.name || eventTypeConfig.label,
        icon: EnvelopeSimple,
      },
      ...itinerary.map((item) => ({
        scope: 'event',
        itineraryEventId: item.id,
        side: item.side || 'both',
        title: item.name || 'Itinerary event',
        subtitle: [formatDate(item.date), displaySideLabel(item.side || 'both')].filter(Boolean).join(' | '),
        icon: CalendarCheck,
      })),
    ];
  }, [displaySideLabel, eventData, eventTypeConfig]);

  const selectedEventInviteItems = useMemo(
    () => eventTypeConfig.features.showItinerary
      ? items.filter((item) => item.invite_scope === 'selected_events' && isInviteVisibleOnMain(item))
      : [],
    [eventTypeConfig.features.showItinerary, items]
  );

  useEffect(() => {
    if (!creatingLooks) {
      setLoadingLookStep(0);
      return undefined;
    }
    const timer = window.setInterval(() => {
      setLoadingLookStep((current) => (current + 1) % LOOK_LOADING_COPY.length);
    }, 1800);
    return () => window.clearInterval(timer);
  }, [creatingLooks]);

  useEffect(() => {
    if (!draftSlot || !Object.keys(draftForm || {}).length) return;
    if (draftSlot.existing?.id) return;
    setDraftCache((cache) => ({
      ...cache,
      [formCacheKey(draftSlot.context, draftSlot.audienceSide, draftSlot.existing)]: draftForm,
    }));
  }, [draftForm, draftSlot]);

  const itineraryById = useMemo(() => {
    const map = new Map();
    (eventData?.itinerary || []).forEach((item) => {
      if (item?.id) map.set(item.id, item);
    });
    return map;
  }, [eventData]);

  const selectedEventNames = (ids = []) => (
    ids.map((id) => itineraryById.get(id)?.name).filter(Boolean).join(' + ') || 'Selected events'
  );

  const selectedEventSide = (ids = []) => {
    const sides = Array.from(new Set(
      ids
        .map((id) => itineraryById.get(id)?.side || 'both')
        .map((side) => String(side).toLowerCase())
        .filter(Boolean)
    ));
    if (sides.length === 1 && ['bride', 'groom', 'both'].includes(sides[0])) return sides[0];
    return 'both';
  };

  const selectedEventSideLabel = (ids = []) => {
    const sides = Array.from(new Set(
      ids
        .map((id) => itineraryById.get(id)?.side || 'both')
        .map((side) => String(side).toLowerCase())
        .filter(Boolean)
    ));
    if (sides.length === 1) return displaySideLabel(sides[0]);
    return ids.length ? 'Mixed selected events' : 'Derived from selected events';
  };

  const loadInvitePacks = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const response = await api.get(`/events/${eventId}/invite-packs`);
      const loadedItems = response.data?.items || [];
      const activeJob = inviteGenerationJobs.get(eventId);
      const pendingItem = activeJob?.status === 'pending' && activeJob.metadata
        ? buildPendingInviteItem(activeJob.metadata)
        : null;
      setItems(pendingItem
        ? [...loadedItems.filter((item) => item.id !== pendingItem.id), pendingItem]
        : loadedItems);
    } catch (error) {
      console.error('Failed to load invite packs', error);
      toast.error(error.response?.data?.detail || 'Failed to load invite packs');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadInvitePacks();
  }, [loadInvitePacks]);

  const upsertItem = (nextItem) => {
    setItems((current) => {
      if (!nextItem?.id) return current;
      const existingIndex = current.findIndex((item) => item.id === nextItem.id);
      if (existingIndex >= 0) {
        return current.map((item, index) => (index === existingIndex ? nextItem : item));
      }
      return [...current, nextItem];
    });
  };

  const openDraft = (context, audienceSide, existing) => {
    const resolvedSide = contextAudienceSide(context, audienceSide);
    const cachedDraft = existing?.id
      ? (draftCache[formCacheKey(context, resolvedSide, existing)] || {})
      : (draftCache[LAST_CREATED_DRAFT_CACHE_KEY] || draftCache[draftCacheKey(context, resolvedSide)] || {});
    setDraftSlot({ context, audienceSide: resolvedSide, existing });
    setDraftForm(normalizeDraftForContext({
      ...defaultDraft(eventData, context, resolvedSide, existing),
      ...cachedDraft,
    }, context, resolvedSide));
    setLookCandidates(existing?.generated_candidates || []);
  };

  const openSelectedEventsDraft = () => {
    const context = {
      scope: 'selected_events',
      itineraryEventId: null,
      itineraryEventIds: [],
      title: 'Create invite',
      subtitle: 'Choose itinerary event(s)',
      icon: CalendarCheck,
    };
    const cachedDraft = draftCache[LAST_CREATED_DRAFT_CACHE_KEY] || draftCache[draftCacheKey(context, 'both')] || {};
    setDraftSlot({ context, audienceSide: 'both', existing: null });
    setDraftForm(normalizeDraftForContext({
      ...defaultDraft(eventData, context, 'both', null),
      ...cachedDraft,
    }, context, 'both'));
    setLookCandidates([]);
  };

  const openCreateDraft = () => {
    if (eventTypeConfig.features.showItinerary && (eventData?.itinerary || []).length > 0) {
      openSelectedEventsDraft();
      return;
    }
    openDraft(contexts[0], 'both', null);
  };

  const openPreview = (context, audienceSide, item) => {
    if (!item?.media_url) return;
    setPreviewSlot({ context, audienceSide, item });
  };

  const openLookChooser = (context, audienceSide, item) => {
    const candidates = (item?.generated_candidates || []).filter((candidate) => candidate?.media_url);
    if (!candidates.length) {
      toast.error('No generated invite options were found for this card');
      return;
    }
    const resolvedSide = contextAudienceSide(context, audienceSide || item?.audience_side);
    setChooseSlot({ context, audienceSide: resolvedSide, existing: item });
    setLookCandidates(candidates);
  };

  const updateDraft = (field, value) => {
    setDraftForm((current) => {
      return { ...current, [field]: value };
    });
  };

  const rememberSubmittedDraft = (form, context, audienceSide, item = null) => {
    const normalized = normalizeDraftForContext(form, context, audienceSide);
    setDraftCache((cache) => ({
      ...cache,
      [LAST_CREATED_DRAFT_CACHE_KEY]: normalized,
      ...(item?.id ? { [formCacheKey(context, audienceSide, item)]: normalized } : {}),
    }));
  };

  useEffect(() => {
    const job = inviteGenerationJobs.get(eventId);
    if (!job) return undefined;

    let attached = true;
    setCreatingLooks(job.status === 'pending');
    job.promise.then(async (completedJob) => {
      if (!attached) return;
      if (completedJob.status === 'success') {
        const candidates = (completedJob.response?.data?.candidates || [])
          .filter((candidate) => candidate?.media_url);
        setLookCandidates(candidates);
        rememberSubmittedDraft(
          completedJob.metadata.form,
          completedJob.metadata.context,
          completedJob.metadata.audienceSide,
        );
        if (candidates.length) {
          const readyItem = buildPendingInviteItem({
            ...completedJob.metadata,
            candidates,
          });
          setItems((current) => {
            const withoutPending = current.filter((item) => item.id !== readyItem.id);
            return [...withoutPending, readyItem];
          });
          toast.success('Invite ready to review');
        } else {
          await loadInvitePacks();
          toast.error('No invite images were generated. Please try again with another photo or design.');
        }
      } else {
        await loadInvitePacks();
        toast.error(completedJob.error?.response?.data?.detail || 'Failed to create invite');
      }
      setCreatingLooks(false);
      if (inviteGenerationJobs.get(eventId) === completedJob) {
        inviteGenerationJobs.delete(eventId);
      }
    });

    return () => {
      attached = false;
    };
  // The version changes only when this mount starts a job; page re-entry reads
  // the existing module-level job for the event.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, generationJobVersion, loadInvitePacks]);

  const toggleDraftItineraryEvent = (itemId, checked) => {
    setDraftForm((current) => {
      const currentIds = Array.isArray(current.itinerary_event_ids) ? current.itinerary_event_ids : [];
      const nextIds = checked
        ? Array.from(new Set([...currentIds, itemId]))
        : currentIds.filter((id) => id !== itemId);
      const nextSide = selectedEventSide(nextIds);
      setDraftSlot((slot) => slot ? { ...slot, audienceSide: nextSide } : slot);
      return {
        ...current,
        itinerary_event_ids: nextIds,
        audience_side: nextSide,
        title: current.title === 'Create invite' || current.title === 'Selected events invite' || !current.title
          ? `${selectedEventNames(nextIds)} invite`
          : current.title,
      };
    });
  };

  const uploadReferencePhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !draftSlot) return;
    setUploadingReferencePhoto(true);
    try {
      const payload = new FormData();
      payload.append('file', file);
      const response = await api.post(`/events/${eventId}/invite-packs/reference-photo`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDraftForm((current) => ({
        ...current,
        reference_photo_url: response.data?.url || '',
        reference_photo_filename: response.data?.original_filename || file.name,
      }));
      toast.success('Person photo added');
    } catch (error) {
      console.error('Failed to upload reference photo', error);
      toast.error(error.response?.data?.detail || 'Failed to upload reference photo');
    } finally {
      setUploadingReferencePhoto(false);
    }
  };

  const removeReferencePhoto = () => {
    setDraftForm((current) => ({
      ...current,
      reference_photo_url: '',
      reference_photo_filename: '',
    }));
  };

  const uploadInspirationPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !draftSlot) return;
    setUploadingInspirationPhoto(true);
    try {
      const payload = new FormData();
      payload.append('file', file);
      const response = await api.post(`/events/${eventId}/invite-packs/reference-photo`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDraftForm((current) => ({
        ...current,
        inspiration_photo_url: response.data?.url || '',
        inspiration_photo_filename: response.data?.original_filename || file.name,
      }));
      toast.success('Inspiration design added');
    } catch (error) {
      console.error('Failed to upload inspiration design', error);
      toast.error(error.response?.data?.detail || 'Failed to upload inspiration design');
    } finally {
      setUploadingInspirationPhoto(false);
    }
  };

  const removeInspirationPhoto = () => {
    setDraftForm((current) => ({
      ...current,
      inspiration_photo_url: '',
      inspiration_photo_filename: '',
    }));
  };

  const buildDraftPayload = () => ({
    invite_scope: draftSlot.context.scope,
    itinerary_event_id: draftSlot.context.itineraryEventId,
    itinerary_event_ids: draftSlot.context.scope === 'selected_events'
      ? (draftForm.itinerary_event_ids || [])
      : [],
    audience_side: contextAudienceSide(draftSlot.context, draftSlot.audienceSide),
    source: 'ai_draft',
    title: draftForm.title,
    caption: draftForm.caption,
    ai_style: draftForm.ai_style,
    ai_language: draftForm.ai_language,
    ai_format: 'image',
    ai_inputs: {
      ...(eventTypeConfig.isWedding
        ? {
          bride_name: draftForm.bride_name,
          groom_name: draftForm.groom_name,
        }
        : { host_names: draftForm.host_names }),
      family_line: draftForm.family_line,
      blessing: draftForm.blessing,
      hashtag: draftForm.hashtag,
      notes: draftForm.inspiration_photo_url ? '' : draftForm.notes,
      reference_photo_url: draftForm.reference_photo_url,
      reference_photo_filename: draftForm.reference_photo_filename,
      inspiration_photo_url: draftForm.inspiration_photo_url,
      inspiration_photo_filename: draftForm.inspiration_photo_filename,
      person_style: draftForm.person_style,
      creativity: draftForm.creativity,
      look_count: 1,
    },
  });

  const createLooks = () => {
    if (!draftSlot) return;
    if (draftSlot.context.scope === 'selected_events' && !(draftForm.itinerary_event_ids || []).length) {
      toast.error('Select at least one event for this combined invite');
      return;
    }
    const activeDraftSlot = draftSlot;
    const activeDraftForm = draftForm;
    const pendingId = `pending-invite-${Date.now()}`;
    const payload = {
      ...buildDraftPayload(),
      look_count: 1,
      person_style: activeDraftForm.person_style || 'caricature',
      creativity: activeDraftForm.creativity || 'bold',
    };
    upsertItem(buildPendingInviteItem({
      id: pendingId,
      payload,
      context: activeDraftSlot.context,
      audienceSide: activeDraftSlot.audienceSide,
    }));
    setCreatingLooks(true);
    setLookCandidates([]);
    setDraftSlot(null);
    startInviteGenerationJob(
      eventId,
      () => api.post(`/events/${eventId}/invite-packs/looks`, payload),
      {
        id: pendingId,
        payload,
        context: activeDraftSlot.context,
        audienceSide: activeDraftSlot.audienceSide,
        form: activeDraftForm,
      },
    );
    setGenerationJobVersion((current) => current + 1);
  };

  const selectLook = async (candidate) => {
    if (!candidate?.id || !candidate?.media_url) {
      toast.error('This look has no generated image to select');
      return;
    }
    const activeSlot = draftSlot || chooseSlot || historySlot;
    const pendingItemId = activeSlot?.existing?.generation_status === 'ready_to_select'
      ? activeSlot.existing.id
      : null;
    setSelectingLookId(candidate.id);
    try {
      const response = await api.post(`/events/${eventId}/invite-packs/looks/${candidate.id}/use`);
      upsertItem(response.data.item);
      if (response.data.item && activeSlot?.context) {
        const formToRemember = draftSlot
          ? draftForm
          : defaultDraft(eventData, activeSlot.context, activeSlot.audienceSide, activeSlot.existing || activeSlot.item);
        rememberSubmittedDraft(
          formToRemember,
          activeSlot.context,
          contextAudienceSide(activeSlot.context, activeSlot.audienceSide),
          response.data.item
        );
      }
      if (pendingItemId) {
        setItems((current) => current.filter((item) => item.id !== pendingItemId));
      }
      toast.success('Invite look selected');
      setDraftSlot(null);
      setChooseSlot(null);
      setLookCandidates([]);
    } catch (error) {
      console.error('Failed to select invite look', error);
      toast.error(error.response?.data?.detail || 'Failed to select invite look');
    } finally {
      setSelectingLookId(null);
    }
  };

  const saveDraft = async () => {
    if (!draftSlot) return;
    if (draftSlot.context.scope === 'selected_events' && !(draftForm.itinerary_event_ids || []).length) {
      toast.error('Select at least one event for this combined invite');
      return;
    }
    setSavingDraft(true);
    try {
      const payload = buildDraftPayload();
      const response = await api.post(`/events/${eventId}/invite-packs/draft`, payload);
      upsertItem(response.data.item);
      rememberSubmittedDraft(draftForm, draftSlot.context, draftSlot.audienceSide, response.data.item);
      toast.success('Invite draft saved');
      setDraftSlot(null);
    } catch (error) {
      console.error('Failed to save invite draft', error);
      toast.error(error.response?.data?.detail || 'Failed to save invite draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const deleteItem = async (item) => {
    try {
      await api.delete(`/events/${eventId}/invite-packs/${item.id}`);
      setItems((current) => current.filter((entry) => entry.id !== item.id));
      toast.success('Invite pack removed');
    } catch (error) {
      console.error('Failed to delete invite pack', error);
      toast.error(error.response?.data?.detail || 'Failed to remove invite pack');
    }
  };

  const openHistory = async (item, context, audienceSide) => {
    if (!item?.id) return;
    setHistorySlot({ item, context, audienceSide });
    setHistoryCandidates([]);
    setHistoryLoading(true);
    try {
      const response = await api.get(`/events/${eventId}/invite-packs/${item.id}/history`);
      setHistoryCandidates((response.data?.candidates || []).filter((candidate) => candidate?.media_url));
    } catch (error) {
      console.error('Failed to load invite history', error);
      toast.error(error.response?.data?.detail || 'Failed to load invite history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const promoteHistoryCandidate = async (candidate) => {
    await selectLook(candidate);
    setHistorySlot(null);
    setHistoryCandidates([]);
  };

  const hasInviteCards = items.some((item) => (
    isInviteVisibleOnMain(item)
    && !item?.is_deleted
    && (eventTypeConfig.features.showItinerary || ['full_event', 'full_wedding'].includes(item.invite_scope))
  ));

  const studioJourney = eventTypeConfig.features.showItinerary ? [
    {
      title: 'Select event(s)',
      hint: 'What to celebrate',
      icon: CalendarCheck,
    },
    {
      title: 'Add invite info',
      hint: 'When and where',
      icon: NotePencil,
    },
    {
      title: 'Generate invite',
      hint: 'AI designs it',
      icon: MagicWand,
    },
    {
      title: 'Select and send',
      hint: 'WhatsApp ready',
      icon: PaperPlaneTilt,
    },
  ] : [
    {
      title: 'Add invite info',
      hint: 'When and where',
      icon: NotePencil,
    },
    {
      title: 'Choose photo style',
      hint: 'Real or illustrated',
      icon: ImageSquare,
    },
    {
      title: 'Generate invite',
      hint: 'AI designs it',
      icon: MagicWand,
    },
    {
      title: 'Select and send',
      hint: 'WhatsApp ready',
      icon: PaperPlaneTilt,
    },
  ];

  if (loading) {
    return (
      <div className="grid min-h-72 place-items-center rounded-md border bg-card text-muted-foreground">
        Loading invite studio...
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Sparkle className="h-5 w-5 text-primary" weight="fill" />
                Invite Studio
              </CardTitle>
            </div>
            <div className="flex flex-wrap gap-2">
              <AIPremiumButton
                type="button"
                featureName="AI invite generation"
                usageCategory={AI_USAGE_IMAGE_GENERATION}
                onClick={openCreateDraft}
              >
                <CalendarCheck className="h-4 w-4" />
                Create Invite
              </AIPremiumButton>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!hasInviteCards && (
            <div className="overflow-hidden rounded-md border bg-gradient-to-br from-background via-background to-muted/30 p-5 shadow-sm">
              <div className="grid gap-5 lg:grid-cols-[1fr_15rem] lg:items-center">
                <div>
                  <h3 className="font-serif text-2xl font-semibold text-foreground">{eventTypeConfig.labels.inviteStudioEmptyTitle}</h3>
                  <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
                    Turn your event details and favorite photo style into a polished, WhatsApp-ready invite.
                  </p>
                  <ol
                    className="relative mt-6 grid grid-cols-4 gap-1 before:absolute before:left-[12.5%] before:right-[12.5%] before:top-6 before:h-px before:bg-border sm:gap-3"
                    aria-label="How Invite Studio works"
                  >
                    {studioJourney.map(({ title, hint, icon: StepIcon }, index) => (
                      <li key={title} className="relative flex min-w-0 flex-col items-center text-center">
                        <span className="relative z-10 grid h-12 w-12 place-items-center rounded-full border border-primary/20 bg-background text-primary shadow-sm ring-4 ring-background">
                          <StepIcon className="h-5 w-5" weight={index === studioJourney.length - 1 ? 'fill' : 'regular'} />
                          <span className="absolute -right-1 -top-1 grid h-5 w-5 place-items-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground ring-2 ring-background">
                            {index + 1}
                          </span>
                        </span>
                        <span className="mt-3 text-xs font-semibold leading-4 text-foreground sm:text-sm">
                          {title}
                        </span>
                        <span className="mt-1 hidden text-[11px] leading-4 text-muted-foreground sm:block">
                          {hint}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
                <div className="rounded-md border bg-[linear-gradient(145deg,#fee47b,#f7b753_48%,#fbf3cd)] p-4 text-center font-serif text-lg font-semibold text-[#6e4e12] shadow-inner">
                  <div className="grid aspect-[4/5] place-items-center rounded-md border border-[#dfc27f] bg-white/30">
                    AI Invite<br />Preview
                    <AIPremiumButton
                      type="button"
                      featureName="AI invite generation"
                      usageCategory={AI_USAGE_IMAGE_GENERATION}
                      onClick={openCreateDraft}
                    >
                      <Sparkle className="h-4 w-4" weight="fill" />
                      Create Invite
                    </AIPremiumButton>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                    
                  </div>
              </div>
            </div>
          )}

          {hasInviteCards && (
            <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
              {contexts.map((context) => {
                const contextItems = items
                  .filter((item) => itemMatchesContext(item, context) && item.invite_scope !== 'selected_events' && isInviteVisibleOnMain(item))
                  .map((item) => ({
                    side: {
                      value: contextAudienceSide(context, item.audience_side || 'default'),
                      label: displaySideLabel(contextAudienceSide(context, item.audience_side || 'default')),
                    },
                    item,
                  }));
                if (contextItems.length === 0) return null;
                return (
                  <InviteContextTile
                    key={`${context.scope}-${context.itineraryEventId || 'full'}`}
                    context={context}
                    badge={context.scope === 'event' ? (
                      <Badge variant="outline" className="shrink-0">Itinerary invite</Badge>
                    ) : null}
                  >
                    {contextItems.map(({ side, item }) => (
                      <InviteSlotCard
                        key={item.id || `${side.value}-${item.media_url || item.preview_media_url || item.title}`}
                        context={context}
                        side={side}
                        item={item}
                        onGenerate={openDraft}
                        onChoose={openLookChooser}
                        onDelete={deleteItem}
                        onPreview={openPreview}
                        onHistory={openHistory}
                        loadingStep={loadingLookStep}
                      />
                    ))}
                  </InviteContextTile>
                );
              })}

              {selectedEventInviteItems.map((item) => {
                const context = {
                  scope: 'selected_events',
                  itineraryEventId: null,
                  itineraryEventIds: item.itinerary_event_ids || [],
                  title: item.title || selectedEventNames(item.itinerary_event_ids),
                  subtitle: selectedEventNames(item.itinerary_event_ids),
                  icon: CalendarCheck,
                };
                return (
                  <InviteContextTile
                    key={item.id}
                    context={context}
                    badge={<Badge variant="outline" className="shrink-0">Custom</Badge>}
                  >
                    <InviteSlotCard
                      context={context}
                      side={{
                        value: item.audience_side || 'both',
                        label: selectedEventSideLabel(item.itinerary_event_ids || []),
                      }}
                      item={item}
                      onGenerate={openDraft}
                      onChoose={openLookChooser}
                      onDelete={deleteItem}
                      onPreview={openPreview}
                      onHistory={openHistory}
                      loadingStep={loadingLookStep}
                    />
                  </InviteContextTile>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(draftSlot)} onOpenChange={(open) => !open && setDraftSlot(null)}>
        <DialogContent className="max-w-2xl gap-3 p-4 sm:p-5">
          <DialogHeader className="space-y-1">
            <DialogTitle>Create invite</DialogTitle>
            <p className="text-sm text-muted-foreground">
              {draftSlot ? `${draftSlot.context.title} - ${displaySideLabel(draftSlot.audienceSide)}` : ''}
            </p>
          </DialogHeader>

          <div className="grid max-h-[68vh] gap-3 overflow-y-auto pr-1">
            {draftSlot?.context?.scope === 'selected_events' && (
              <div className="rounded-md border bg-muted/20 p-3">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Label>Events included</Label>
                    <Badge variant="outline">{selectedEventSideLabel(draftForm.itinerary_event_ids || [])}</Badge>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {(eventData?.itinerary || []).map((item) => {
                      const checked = (draftForm.itinerary_event_ids || []).includes(item.id);
                      return (
                        <label key={item.id} className="flex items-start gap-2 rounded-md border bg-background p-2 text-sm">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(value) => toggleDraftItineraryEvent(item.id, Boolean(value))}
                          />
                          <span className="min-w-0">
                            <span className="block truncate font-medium">{item.name || 'Itinerary event'}</span>
                            <span className="block text-xs text-muted-foreground">
                              {[formatDate(item.date), displaySideLabel(item.side || 'both')].filter(Boolean).join(' | ')}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_10rem]">
              <div className="space-y-1.5">
                <Label htmlFor="invite-title">Invite title</Label>
                <Input
                  id="invite-title"
                  value={draftForm.title || ''}
                  onChange={(event) => updateDraft('title', event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Language</Label>
                <Select value={draftForm.ai_language || 'english'} onValueChange={(value) => updateDraft('ai_language', value)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LANGUAGE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {eventTypeConfig.isWedding ? <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Bride name</Label>
                <Input value={draftForm.bride_name || ''} onChange={(event) => updateDraft('bride_name', event.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Groom name</Label>
                <Input value={draftForm.groom_name || ''} onChange={(event) => updateDraft('groom_name', event.target.value)} />
              </div>
            </div> : <div className="space-y-2">
              <Label>{eventTypeConfig.isBirthday ? eventTypeConfig.labels.honoreeNameLabel : eventTypeConfig.labels.hostNameLabel}</Label>
              <Input
                value={draftForm.host_names || ''}
                onChange={(event) => updateDraft('host_names', event.target.value)}
                placeholder="Separate multiple names with commas"
              />
            </div>}

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Family or host line</Label>
                <Input
                  placeholder={eventTypeConfig.labels.inviteFamilyLinePlaceholder}
                  value={draftForm.family_line || ''}
                  onChange={(event) => updateDraft('family_line', event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Hashtag</Label>
                <Input
                  placeholder="e.g. #AnjanaRahul"
                  value={draftForm.hashtag || ''}
                  onChange={(event) => updateDraft('hashtag', event.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Blessing or personal note</Label>
              <Input
                placeholder="e.g. With blessings from both families"
                value={draftForm.blessing || ''}
                onChange={(event) => updateDraft('blessing', event.target.value)}
              />
            </div>

            <div className="grid gap-2 md:grid-cols-2">
              <div className="grid gap-3 rounded-md border bg-muted/20 p-2.5 sm:grid-cols-[5rem_1fr]">
                <div className="grid aspect-[4/3] place-items-center overflow-hidden rounded-md border bg-background">
                  {draftForm.reference_photo_url ? (
                    <img
                      src={resolveMediaUrl(draftForm.reference_photo_url, { preferProxy: true })}
                      alt="Person photo for invite"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <ImageSquare className="h-7 w-7 text-muted-foreground" />
                  )}
                </div>
                <div className="min-w-0 space-y-2">
                  <div>
                    <Label htmlFor="invite-reference-photo">Person photo</Label>
                    <p className="mt-0.5 line-clamp-2 text-xs leading-4 text-muted-foreground">
                      {draftForm.reference_photo_filename || 'Upload the person, pair, or group to use. AI will not add anyone else.'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button asChild type="button" size="sm" variant="outline" disabled={uploadingReferencePhoto}>
                      <label htmlFor="invite-reference-photo" className="cursor-pointer">
                        <UploadSimple className="h-4 w-4" />
                        {uploadingReferencePhoto ? 'Uploading' : draftForm.reference_photo_url ? 'Replace photo' : 'Upload photo'}
                      </label>
                    </Button>
                    {draftForm.reference_photo_url && (
                      <Button type="button" size="sm" variant="ghost" onClick={removeReferencePhoto}>
                        Remove
                      </Button>
                    )}
                  </div>
                  <input
                    id="invite-reference-photo"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={uploadReferencePhoto}
                  />
                </div>
              </div>

              <div className="grid gap-3 rounded-md border bg-muted/20 p-2.5 sm:grid-cols-[5rem_1fr]">
                <div className="grid aspect-[4/3] place-items-center overflow-hidden rounded-md border bg-background">
                  {draftForm.inspiration_photo_url ? (
                    <img
                      src={resolveMediaUrl(draftForm.inspiration_photo_url, { preferProxy: true })}
                      alt="Inspiration design for invite"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <ImageSquare className="h-7 w-7 text-muted-foreground" />
                  )}
                </div>
                <div className="min-w-0 space-y-2">
                  <div>
                    <Label htmlFor="invite-inspiration-photo">Inspiration design</Label>
                    <p className="mt-0.5 line-clamp-2 text-xs leading-4 text-muted-foreground">
                      {draftForm.inspiration_photo_filename || 'Upload a sample invite. AI borrows the art direction, not its people or text.'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button asChild type="button" size="sm" variant="outline" disabled={uploadingInspirationPhoto}>
                      <label htmlFor="invite-inspiration-photo" className="cursor-pointer">
                        <UploadSimple className="h-4 w-4" />
                        {uploadingInspirationPhoto ? 'Uploading' : draftForm.inspiration_photo_url ? 'Replace design' : 'Upload design'}
                      </label>
                    </Button>
                    {draftForm.inspiration_photo_url && (
                      <Button type="button" size="sm" variant="ghost" onClick={removeInspirationPhoto}>
                        Remove
                      </Button>
                    )}
                  </div>
                  <input
                    id="invite-inspiration-photo"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={uploadInspirationPhoto}
                  />
                </div>
              </div>
            </div>

            <div className="rounded-md border bg-background p-2.5">
              <div className="flex flex-col gap-1">
                <p className="text-sm font-semibold text-foreground">Creative look lab</p>
                <p className="text-xs leading-5 text-muted-foreground">
                  Choose how the single image invite should represent the uploaded people.
                </p>
              </div>
              <div className="mt-2 grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Photo mode</Label>
                  <Select value={draftForm.person_style || 'caricature'} onValueChange={(value) => updateDraft('person_style', value)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PERSON_STYLE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Creativity</Label>
                  <Select value={draftForm.creativity || 'bold'} onValueChange={(value) => updateDraft('creativity', value)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CREATIVITY_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className={`grid gap-3 ${draftForm.inspiration_photo_url ? '' : 'sm:grid-cols-2'}`}>
              <div className="space-y-2">
                <Label>Caption override</Label>
                <Textarea
                  rows={2}
                  placeholder="Leave empty to draft from event details."
                  value={draftForm.caption || ''}
                  onChange={(event) => updateDraft('caption', event.target.value)}
                />
              </div>
              {!draftForm.inspiration_photo_url && (
                <div className="space-y-2">
                  <Label>Creative direction</Label>
                  <Textarea
                    rows={2}
                    placeholder="e.g. Rajasthani palace, marigolds, gold typography"
                    value={draftForm.notes || ''}
                    onChange={(event) => updateDraft('notes', event.target.value)}
                  />
                </div>
              )}
            </div>

            {(creatingLooks || lookCandidates.length > 0) && (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      {creatingLooks ? 'AI is creating your invite' : 'Generated invite'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {creatingLooks
                        ? 'Preview cards will turn into selectable invite images as soon as generation finishes.'
                        : 'Preview, compare, and select one for this invite slot.'}
                    </p>
                  </div>
                  <Badge variant="secondary">
                    {creatingLooks ? 'Creating invite' : `${lookCandidates.length} option${lookCandidates.length === 1 ? '' : 's'}`}
                  </Badge>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  {creatingLooks && Array.from({ length: 1 }).map((_, index) => (
                    <LoadingLookCard key={`loading-look-${index}`} index={index} step={loadingLookStep} />
                  ))}
                  {!creatingLooks && lookCandidates.map((candidate) => {
                    const concept = candidate.creative_concept || {};
                    const mediaUrl = candidate.media_url ? resolveMediaUrl(candidate.media_url, { preferProxy: true }) : null;
                    return (
                      <div key={candidate.id} className="overflow-hidden rounded-md border bg-card">
                        <button
                          type="button"
                          className="block aspect-[4/5] w-full bg-muted/30 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                          onClick={() => candidate.media_url && setPreviewSlot({ context: draftSlot.context, audienceSide: draftSlot.audienceSide, item: candidate })}
                          aria-label={`Preview generated look ${candidate.title || 'invite look'}`}
                        >
                      {mediaUrl ? (
                            <img
                              src={mediaUrl}
                              alt={candidate.title || 'Generated invite look'}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="grid h-full place-items-center text-muted-foreground">
                              <Sparkle className="h-8 w-8" weight="fill" />
                            </div>
                          )}
                        </button>
                        <div className="space-y-3 p-3">
                          <div>
                            <p className="line-clamp-1 text-sm font-semibold">{candidate.title || concept.name || 'Invite look'}</p>
                            <p className="mt-1 line-clamp-2 text-xs leading-5 text-muted-foreground">
                              {concept.visual_style || candidate.generation_status || `Fresh AI-generated ${eventTypeConfig.labels.invitationSubject} invite direction.`}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => selectLook(candidate)}
                              disabled={selectingLookId === candidate.id || !candidate.media_url}
                            >
                              {selectingLookId === candidate.id ? 'Selecting...' : 'Select invitation'}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => candidate.media_url && setPreviewSlot({ context: draftSlot.context, audienceSide: draftSlot.audienceSide, item: candidate })}
                              disabled={!candidate.media_url}
                            >
                              Preview
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDraftSlot(null)}>Cancel</Button>
            <AIPremiumButton
              type="button"
              featureName="AI invite look generation"
              usageCategory={AI_USAGE_IMAGE_GENERATION}
              onClick={createLooks}
              disabled={creatingLooks || uploadingReferencePhoto || uploadingInspirationPhoto}
            >
              <Sparkle className="h-4 w-4" weight="fill" />
              {creatingLooks ? 'Creating...' : 'Create invite'}
            </AIPremiumButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(chooseSlot)} onOpenChange={(open) => !open && setChooseSlot(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Review invitation design</DialogTitle>
            <p className="text-sm text-muted-foreground">
              {chooseSlot ? `${chooseSlot.context.title} - ${displaySideLabel(chooseSlot.audienceSide)}` : ''}
            </p>
          </DialogHeader>

          <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-foreground">Generated looks</p>
                <p className="text-xs text-muted-foreground">
                  Select a design to save it in Invite Studio for sending later. This does not send an invitation.
                </p>
              </div>
              <Badge variant="secondary">{lookCandidates.length} options</Badge>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {lookCandidates.map((candidate) => {
                const concept = candidate.creative_concept || {};
                const mediaUrl = candidate.media_url ? resolveMediaUrl(candidate.media_url, { preferProxy: true }) : null;
                return (
                  <div key={candidate.id} className="overflow-hidden rounded-md border bg-card">
                    <button
                      type="button"
                      className="block aspect-[4/5] w-full bg-muted/30 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      onClick={() => candidate.media_url && setPreviewSlot({ context: chooseSlot.context, audienceSide: chooseSlot.audienceSide, item: candidate })}
                      aria-label={`Preview generated look ${candidate.title || 'invite look'}`}
                    >
                      {mediaUrl ? (
                        <img
                          src={mediaUrl}
                          alt={candidate.title || 'Generated invite look'}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="grid h-full place-items-center text-muted-foreground">
                          <Sparkle className="h-8 w-8" weight="fill" />
                        </div>
                      )}
                    </button>
                    <div className="space-y-3 p-3">
                      <div>
                        <p className="line-clamp-1 text-sm font-semibold">{candidate.title || concept.name || 'Invite look'}</p>
                        <p className="mt-1 line-clamp-2 text-xs leading-5 text-muted-foreground">
                          {concept.visual_style || candidate.generation_status || `Fresh AI-generated ${eventTypeConfig.labels.invitationSubject} invite direction.`}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => selectLook(candidate)}
                          disabled={selectingLookId === candidate.id || !candidate.media_url}
                        >
                          {selectingLookId === candidate.id ? 'Selecting...' : 'Select invitation'}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => candidate.media_url && setPreviewSlot({ context: chooseSlot.context, audienceSide: chooseSlot.audienceSide, item: candidate })}
                          disabled={!candidate.media_url}
                        >
                          Preview
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setChooseSlot(null)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(previewSlot)} onOpenChange={(open) => !open && setPreviewSlot(null)}>
        <DialogContent className="flex max-h-[calc(100dvh-1rem)] w-[calc(100%-1rem)] max-w-5xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="border-b px-5 py-4 pr-12">
            <DialogTitle className="truncate">
              {invitationTitle(previewSlot?.item?.title, previewSlot?.context?.title || 'Invite preview')}
            </DialogTitle>
            <DialogDescription>
              {[previewSlot?.context?.title, displaySideLabel(previewSlot?.audienceSide)].filter(Boolean).join(' - ')}
            </DialogDescription>
          </DialogHeader>

          {previewSlot?.item?.media_url && (
            <div className="min-h-0 flex-1 overflow-auto bg-muted/30 p-4">
              <div className="flex min-h-[50vh] items-center justify-center">
                {previewSlot.item.media_type === 'video' ? (
                  <video
                    className="max-h-[calc(100dvh-12rem)] max-w-full rounded-md border bg-background object-contain"
                    controls
                    src={resolveMediaUrl(previewSlot.item.media_url, { preferProxy: true })}
                  >
                    <track kind="captions" />
                  </video>
                ) : (
                  <img
                    src={resolveMediaUrl(previewSlot.item.media_url, { preferProxy: true })}
                    alt={previewSlot.item.title || 'Invite media'}
                    className="max-h-[calc(100dvh-12rem)] max-w-full rounded-md border bg-background object-contain"
                  />
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(historySlot)} onOpenChange={(open) => !open && setHistorySlot(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Invite history</DialogTitle>
            <DialogDescription>
              {historySlot ? `${historySlot.item?.title || historySlot.context?.title || 'Invite'} - ${displaySideLabel(historySlot.audienceSide)}` : ''}
            </DialogDescription>
          </DialogHeader>

          {historyLoading ? (
            <div className="grid min-h-40 place-items-center rounded-md border bg-muted/20 text-sm text-muted-foreground">
              Loading generated looks...
            </div>
          ) : historyCandidates.length === 0 ? (
            <div className="grid min-h-40 place-items-center rounded-md border bg-muted/20 text-sm text-muted-foreground">
              No generated history found for this invite yet.
            </div>
          ) : (
            <div className="grid max-h-[70vh] gap-3 overflow-y-auto pr-1 md:grid-cols-2">
              {historyCandidates.map((candidate) => {
                const isSelected = candidate.id === historySlot?.item?.selected_candidate_id;
                const mediaUrl = candidate.media_url ? resolveMediaUrl(candidate.media_url, { preferProxy: true }) : null;
                return (
                  <div key={candidate.id} className="overflow-hidden rounded-md border bg-card">
                    <button
                      type="button"
                      className="block aspect-[4/5] w-full bg-muted/30 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      onClick={() => candidate.media_url && setPreviewSlot({ context: historySlot.context, audienceSide: historySlot.audienceSide, item: candidate })}
                      aria-label={`Preview historical invite ${candidate.title || 'invite look'}`}
                    >
                      {mediaUrl ? (
                        <img src={mediaUrl} alt={candidate.title || 'Invite history'} className="h-full w-full object-cover" />
                      ) : (
                        <div className="grid h-full place-items-center text-muted-foreground">
                          <Sparkle className="h-8 w-8" weight="fill" />
                        </div>
                      )}
                    </button>
                    <div className="space-y-3 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="line-clamp-1 text-sm font-semibold">{candidate.title || 'Invite look'}</p>
                          <p className="mt-1 text-xs text-muted-foreground">{isSelected ? 'Primary selected invite' : 'Previously generated'}</p>
                        </div>
                        {isSelected && <Badge variant="secondary">Primary</Badge>}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => candidate.media_url && setPreviewSlot({ context: historySlot.context, audienceSide: historySlot.audienceSide, item: candidate })}
                          disabled={!candidate.media_url}
                        >
                          Preview
                        </Button>
                        {!isSelected && (
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => promoteHistoryCandidate(candidate)}
                            disabled={selectingLookId === candidate.id}
                          >
                            {selectingLookId === candidate.id ? 'Selecting...' : 'Set primary'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
